import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import {
  IMPORTANCE_LABELS,
  type ImportanceLevel,
} from '../lib/importanceWeights';

export type ImportanceSegmentedControlProps = {
  /** Distinct `name` per wizard step so native radio groups stay isolated. */
  name: string;
  labelId: string;
  value: ImportanceLevel | null;
  onChange: (value: ImportanceLevel) => void;
  disabled?: boolean;
  className?: string;
};

export function ImportanceSegmentedControl({
  name,
  labelId,
  value,
  onChange,
  disabled = false,
  className,
}: ImportanceSegmentedControlProps) {
  const { t } = useTranslation('matching');
  return (
    <div
      role='radiogroup'
      aria-labelledby={labelId}
      className={cn('flex flex-wrap gap-2', className)}
    >
      {IMPORTANCE_LABELS.map(({ value: level }) => {
        const selected = value === level;
        const inputId = `${name}-${level}`;
        return (
          <div key={level} className='relative'>
            <input
              id={inputId}
              type='radio'
              name={name}
              value={level}
              checked={selected}
              disabled={disabled}
              onChange={() => onChange(level)}
              className='peer sr-only'
            />
            <label
              htmlFor={inputId}
              className={cn(
                'flex min-h-11 min-w-[5.5rem] cursor-pointer items-center justify-center rounded-lg px-4 py-2 text-center text-sm font-medium transition-colors',
                'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary-600',
                'peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
                selected
                  ? 'bg-primary-600 text-primary-foreground shadow-sm hover:bg-primary-700'
                  : 'bg-transparent text-foreground ring-1 ring-border hover:bg-muted',
              )}
            >
              {t(`quiz.importance.${level}`)}
            </label>
          </div>
        );
      })}
    </div>
  );
}
